import { CRANK, HUB_Y, POSTS } from '../../sim/constants';
import { TAU, drawReeds, drawWater, ell, fillStroke, hash01, hills, skyGradient } from '../common';
import { smallPads } from './pond';
import type { DrawCtx, SceneArt } from './types';

/** The heron stands behind the right post and turns the rope with its beak. */
function heron(ctx: CanvasRenderingContext2D, phase: number, t: number): void {
  // Stands in the water behind the pad, between the frog and the right post,
  // and turns the rope with its beak on the crank handle.
  const endY = HUB_Y + CRANK * Math.cos(TAU * phase);
  const crankX = POSTS[1];
  const hx = crankX - 26; // head center
  const hy = endY - 2;
  const bx = 612;
  const by = 300;
  // legs
  ctx.strokeStyle = '#4C4A5C';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(bx - 6, by + 12);
  ctx.lineTo(bx - 10, 372);
  ctx.moveTo(bx + 6, by + 12);
  ctx.lineTo(bx + 8, 372);
  ctx.stroke();
  // body and wing
  ell(ctx, bx, by, 30, 17, 0.2);
  fillStroke(ctx, '#9AAEC2', '#4C4A5C', 2.5);
  ell(ctx, bx - 22, by + 6, 14, 7, 0.6);
  ctx.fillStyle = '#6E8194';
  ctx.fill();
  // neck
  ctx.strokeStyle = '#B9C8D6';
  ctx.lineWidth = 9;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(bx + 20, by - 8);
  ctx.bezierCurveTo(bx + 36, by - 50 + Math.sin(t * 2) * 3, hx - 30, hy - 20, hx - 6, hy);
  ctx.stroke();
  // crest
  ctx.strokeStyle = '#3C3A4A';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(hx - 6, hy - 4);
  ctx.lineTo(hx - 22, hy - 10);
  ctx.stroke();
  // head and beak touching the crank handle
  ell(ctx, hx - 2, hy, 9, 7);
  fillStroke(ctx, '#C9D6E2', '#4C4A5C', 2);
  ctx.fillStyle = '#F2C14E';
  ctx.beginPath();
  ctx.moveTo(hx + 5, hy - 3);
  ctx.lineTo(crankX - 3, endY);
  ctx.lineTo(hx + 5, hy + 3);
  ctx.closePath();
  ctx.fill();
  ell(ctx, hx, hy - 2, 1.8, 1.8);
  ctx.fillStyle = '#1B1B22';
  ctx.fill();
}

export const heronNest: SceneArt = {
  id: 'heron',
  rope: '#F4E3B1',
  ropeBack: '#C2AE7C',
  handle: '#F2C14E',
  ring: 'rgba(255,240,210,0.85)',
  ground: {
    waterTop: '#7A5F86', waterBottom: '#2E2440', shimmer: 'rgba(255,210,170,0.25)',
    padTop: '#5E8F5A', padEdge: '#2C4A30', padVein: 'rgba(30,50,30,0.4)',
    post: '#6B4A3A', postDark: '#523628', postCap: '#8E6A52', ripple: 'rgba(255,220,190,0.5)',
  },
  drawBack(d: DrawCtx) {
    const { ctx, v, t } = d;
    skyGradient(ctx, v, [[0, '#5B3E7A'], [0.55, '#C0607A'], [1, '#F6B26B']]);
    const glow = ctx.createRadialGradient(420, 340, 10, 420, 340, 150);
    glow.addColorStop(0, 'rgba(255,170,90,0.9)');
    glow.addColorStop(1, 'rgba(255,170,90,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(270, 190, 300, 300);
    ell(ctx, 420, 345, 52, 52);
    ctx.fillStyle = '#FF8E4F';
    ctx.fill();
    hills(ctx, v.L, v.R, 354, 26, 0.012, 2.7, '#5A3D5E');
    hills(ctx, v.L, v.R, 363, 14, 0.023, 0.8, '#432E4A');
    drawWater(ctx, v, t, this.ground);
    smallPads(ctx, '#3F6A45');
    drawReeds(ctx, 10, 5, t, '#3B4A2E', '#4A2E20', 4);
    heron(ctx, d.phase, d.reduced ? 0 : t);
  },
  drawFront(d: DrawCtx) {
    const { ctx, v, t } = d;
    // drifting feathers
    const W = v.R - v.L;
    for (let i = 0; i < (d.reduced ? 3 : 8); i++) {
      const x = v.L + ((hash01(i) * W + t * 18) % W);
      const y = 150 + ((hash01(i + 5) * 260 + t * 22) % 280);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.sin(t * 1.5 + i) * 0.8);
      ell(ctx, 0, 0, 7, 2.2);
      ctx.fillStyle = 'rgba(235,240,250,0.85)';
      ctx.fill();
      ctx.restore();
    }
    // tall reeds in the foreground at both edges
    ctx.fillStyle = '#2A2033';
    for (const [x0, dir] of [[v.L, 1], [v.R, -1]] as const) {
      for (let i = 0; i < 4; i++) {
        const x = x0 + dir * (i * 7 + 3);
        const h = 230 + hash01(i + (dir > 0 ? 0 : 20)) * 120;
        const sw = d.reduced ? 0 : Math.sin(t * 1.1 + i) * 6;
        ctx.beginPath();
        ctx.moveTo(x - 3, v.B);
        ctx.quadraticCurveTo(x, v.B - h * 0.6, x + sw, v.B - h);
        ctx.quadraticCurveTo(x + 2, v.B - h * 0.6, x + 3, v.B);
        ctx.fill();
      }
    }
  },
};
